import React from "react";
import {
  Clock, ChefHat, ShoppingCart, Check, Star,
  X, Flame, CalendarPlus, ChevronLeft, ChevronRight,
  BookOpen, ExternalLink, Eye, MoveRight, Trash2,
  Calendar as CalendarIcon, Users, Bell, ListPlus, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Beef } from "lucide-react";
import { useState } from "react";

// Reusable image with text fallback. Avoids stock-photo confusion — if the
// recipe URL fails OR is missing, render a small dashed-border text card
// instead of a generic food photo, so users don't mistake a placeholder for
// the actual recipe.
const SafeMealImage = ({ src, alt, className }) => {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div
        className={`${className} flex flex-col items-center justify-center text-center bg-gray-100 border border-dashed border-gray-300 text-gray-500 p-2`}
        data-testid="meal-image-fallback"
      >
        <span className="text-xs font-semibold leading-tight">We're working on it</span>
        <span className="text-[10px] leading-tight">Refresh to retry</span>
      </div>
    );
  }
  return (
    <img src={src} alt={alt} className={`${className} object-cover`} onError={() => setErrored(true)} />
  );
};

/* ─────────── Swipeable Meal Modal ─────────── */
export const SwipeableMealModal = ({ ctx }) => {
  const {
    selectedMealType, selectedDayIndex, currentMealIndex,
    getMealSuggestionsForDay, weekDays, closeMealModal,
    onTouchStart, onTouchMove, onTouchEnd, getMealImage,
    favoriteMeals, toggleFavoriteMeal,
    navigateToPrevMeal, navigateToNextMeal,
    handleSwipeLeft, handleSwipeRight
  } = ctx;

  if (!selectedMealType) return null;
  const dayMeals = getMealSuggestionsForDay(selectedDayIndex, selectedMealType);
  if (!dayMeals || dayMeals.length === 0) return null;
  const currentMeal = dayMeals[currentMealIndex];
  if (!currentMeal) return null;

  const instructionsUrl = currentMeal.source_url || currentMeal.spoonacular_url;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold capitalize">
            {weekDays[selectedDayIndex]} {selectedMealType}
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {currentMealIndex + 1} of {dayMeals.length}
            </span>
            <Button variant="ghost" size="sm" onClick={closeMealModal} className="p-1">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="relative" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div className="p-6">
            <div className="relative mb-4">
              <SafeMealImage src={getMealImage(currentMeal)} alt={currentMeal.name} className="w-full h-48 rounded-xl" />
              <div
                className={`absolute top-3 right-3 rounded-full p-2 shadow-md cursor-pointer transition-colors ${
                  favoriteMeals[currentMeal.name] ? "bg-yellow-400" : "bg-white hover:bg-yellow-50"
                }`}
                onClick={(e) => { e.stopPropagation(); toggleFavoriteMeal(currentMeal); }}
                data-testid="favorite-meal-btn"
              >
                <Star className={`h-4 w-4 ${favoriteMeals[currentMeal.name] ? "text-white fill-current" : "text-yellow-500"}`} />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">{currentMeal.name}</h3>
              <div className="flex gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{currentMeal.cook_time}</span>
                <span className="capitalize">{currentMeal.difficulty}</span>
                <span className="flex items-center gap-1"><Flame className="h-4 w-4 text-orange-500" />{currentMeal.calories} cal</span>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl border border-green-200">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Beef className="h-4 w-4 text-green-600" />Nutrition Facts
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-2 rounded-lg"><p className="text-gray-500 text-xs">Calories</p><p className="font-bold text-gray-900">{currentMeal.calories} kcal</p></div>
                  <div className="bg-white p-2 rounded-lg"><p className="text-gray-500 text-xs">Protein</p><p className="font-bold text-gray-900">{currentMeal.protein_g}g</p></div>
                  <div className="bg-white p-2 rounded-lg"><p className="text-gray-500 text-xs">Carbs</p><p className="font-bold text-gray-900">{currentMeal.carbs_g}g</p></div>
                  <div className="bg-white p-2 rounded-lg"><p className="text-gray-500 text-xs">Fat</p><p className="font-bold text-gray-900">{currentMeal.fat_g}g</p></div>
                </div>
              </div>

              {currentMeal.base_ingredients && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Key Ingredients</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentMeal.base_ingredients.map((ingredient, idx) => (
                      <Badge key={idx} variant="outline" className="capitalize">{ingredient}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t">
          <div className="flex justify-between items-center mb-4">
            <Button variant="outline" size="sm" onClick={navigateToPrevMeal} className="flex items-center gap-1">
              <ChevronLeft className="h-4 w-4" />Previous
            </Button>
            {instructionsUrl && (
              <Button variant="default" size="sm" onClick={() => window.open(instructionsUrl, '_blank', 'noopener,noreferrer')} className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white">
                <BookOpen className="h-4 w-4" />Instructions<ExternalLink className="h-3 w-3" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={navigateToNextMeal} className="flex items-center gap-1">
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50" onClick={handleSwipeLeft}>
              <X className="h-4 w-4" />Not Interested
            </Button>
            <Button className="flex-1 flex items-center gap-2 bg-green-600 hover:bg-green-700" onClick={handleSwipeRight}>
              <CalendarPlus className="h-4 w-4" />Add to Calendar
            </Button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">Swipe left for "Not Interested" &bull; Swipe right for "Add to Calendar"</p>
        </div>
      </div>
    </div>
  );
};

/* ─────────── Add to Calendar Modal ─────────── */
export const AddToCalendarModal = ({ ctx }) => {
  const {
    showAddToCalendarModal, mealToAdd, closeAddToCalendarModal,
    addToCalendarSuccess, selectedCalendarDate, setSelectedCalendarDate,
    handleAddToCalendarConfirm, selectedMealType, getMealImage
  } = ctx;

  if (!showAddToCalendarModal || !mealToAdd) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-2xl">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><CalendarPlus className="h-5 w-5" />Add to Calendar</h2>
          <Button variant="ghost" size="sm" onClick={closeAddToCalendarModal} className="p-1 text-white hover:bg-white/20"><X className="h-5 w-5" /></Button>
        </div>

        {addToCalendarSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="h-8 w-8 text-green-600" /></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Added to Calendar!</h3>
            <p className="text-gray-600">
              "{mealToAdd.name}" has been scheduled for {selectedCalendarDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <SafeMealImage src={getMealImage(mealToAdd)} alt={mealToAdd.name} className="w-16 h-16 rounded-lg" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900 line-clamp-2">{mealToAdd.name}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <span className="capitalize">{selectedMealType}</span>
                  <span>&bull;</span>
                  <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-orange-500" />{mealToAdd.calories} cal</span>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Quick Select</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={selectedCalendarDate?.toDateString() === new Date().toDateString() ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCalendarDate(new Date())}
                  className={selectedCalendarDate?.toDateString() === new Date().toDateString() ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  <CalendarIcon className="h-4 w-4 mr-1" />Today
                </Button>
                {(() => {
                  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
                  return (
                    <Button
                      variant={selectedCalendarDate?.toDateString() === tomorrow.toDateString() ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCalendarDate(tomorrow)}
                      className={selectedCalendarDate?.toDateString() === tomorrow.toDateString() ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      <CalendarIcon className="h-4 w-4 mr-1" />Tomorrow
                    </Button>
                  );
                })()}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Or Select a Date</p>
              <div className="flex justify-center border rounded-xl p-2 bg-gray-50">
                <Calendar mode="single" selected={selectedCalendarDate} onSelect={setSelectedCalendarDate} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-md" />
              </div>
            </div>

            {selectedCalendarDate && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-green-800">
                  <span className="font-medium">Selected: </span>
                  {selectedCalendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  <span className="text-green-600"> ({selectedMealType})</span>
                </p>
              </div>
            )}

            <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg" onClick={handleAddToCalendarConfirm} disabled={!selectedCalendarDate}>
              <Check className="h-5 w-5 mr-2" />Confirm & Add to Calendar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────── Scheduled Meal View/Edit Modal ─────────── */
export const ScheduledMealModal = ({ ctx }) => {
  const {
    showScheduledMealModal, selectedScheduledMeal, closeScheduledMealModal,
    scheduledMealKey, showMoveModal, setShowMoveModal, moveTargetDate,
    setMoveTargetDate, handleMoveScheduledMeal, showDeleteConfirm,
    setShowDeleteConfirm, handleDeleteScheduledMeal, getMealImage, openGroceryModal
  } = ctx;

  if (!showScheduledMealModal || !selectedScheduledMeal) return null;

  if (showMoveModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Eye className="h-5 w-5" />Scheduled Meal</h2>
            <Button variant="ghost" size="sm" onClick={closeScheduledMealModal} className="p-1 text-white hover:bg-white/20"><X className="h-5 w-5" /></Button>
          </div>
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><MoveRight className="h-5 w-5 text-blue-600" />Move to Another Date</h3>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <SafeMealImage src={getMealImage(selectedScheduledMeal)} alt={selectedScheduledMeal.name} className="w-12 h-12 rounded-lg" />
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm line-clamp-1">{selectedScheduledMeal.name}</p>
                <p className="text-xs text-gray-500">Currently: {scheduledMealKey?.split('-').slice(0, 3).join('-')}</p>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Select New Date</p>
              <div className="flex justify-center border rounded-xl p-2 bg-gray-50">
                <Calendar mode="single" selected={moveTargetDate} onSelect={setMoveTargetDate} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-md" />
              </div>
            </div>
            {moveTargetDate && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-blue-800"><span className="font-medium">Move to: </span>{moveTargetDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setShowMoveModal(false); setMoveTargetDate(null); }}>Cancel</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleMoveScheduledMeal} disabled={!moveTargetDate}><Check className="h-4 w-4 mr-2" />Confirm Move</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Eye className="h-5 w-5" />Scheduled Meal</h2>
            <Button variant="ghost" size="sm" onClick={closeScheduledMealModal} className="p-1 text-white hover:bg-white/20"><X className="h-5 w-5" /></Button>
          </div>
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="h-8 w-8 text-red-600" /></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Meal?</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to remove "{selectedScheduledMeal.name}" from your calendar?</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleDeleteScheduledMeal}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const dateParts = scheduledMealKey?.split('-');
  const dateStr = dateParts?.length >= 3 ? `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}` : null;
  const dateDisplay = dateStr ? new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Unknown';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Eye className="h-5 w-5" />Scheduled Meal</h2>
          <Button variant="ghost" size="sm" onClick={closeScheduledMealModal} className="p-1 text-white hover:bg-white/20"><X className="h-5 w-5" /></Button>
        </div>
        <div className="p-4">
          <div className="relative rounded-xl overflow-hidden mb-4">
            <SafeMealImage src={getMealImage(selectedScheduledMeal)} alt={selectedScheduledMeal.name} className="w-full h-48" />
            <div className="absolute top-2 right-2"><Badge className="bg-blue-600 text-white capitalize">{scheduledMealKey?.split('-').pop()}</Badge></div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedScheduledMeal.name}</h3>
          <div className="flex items-center gap-2 text-gray-600 mb-4"><CalendarIcon className="h-4 w-4" /><span className="text-sm">Scheduled for: {dateDisplay}</span></div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-orange-50 rounded-lg p-3 text-center"><Flame className="h-5 w-5 text-orange-500 mx-auto mb-1" /><div className="text-lg font-bold text-gray-900">{selectedScheduledMeal.calories}</div><div className="text-xs text-gray-500">Calories</div></div>
            <div className="bg-blue-50 rounded-lg p-3 text-center"><Clock className="h-5 w-5 text-blue-500 mx-auto mb-1" /><div className="text-lg font-bold text-gray-900">{selectedScheduledMeal.time || selectedScheduledMeal.readyInMinutes || '30'}</div><div className="text-xs text-gray-500">Minutes</div></div>
            <div className="bg-green-50 rounded-lg p-3 text-center"><Users className="h-5 w-5 text-green-500 mx-auto mb-1" /><div className="text-lg font-bold text-gray-900">{selectedScheduledMeal.servings || '4'}</div><div className="text-xs text-gray-500">Servings</div></div>
          </div>

          {(selectedScheduledMeal.nutrition || selectedScheduledMeal.calories) && (
            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Nutrition Facts</h4>
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div><div className="font-bold text-gray-900">{selectedScheduledMeal.nutrition?.calories || selectedScheduledMeal.calories || '0'}</div><div className="text-gray-500">kcal</div></div>
                <div><div className="font-bold text-gray-900">{selectedScheduledMeal.nutrition?.protein_g || selectedScheduledMeal.protein_g || '0'}g</div><div className="text-gray-500">Protein</div></div>
                <div><div className="font-bold text-gray-900">{selectedScheduledMeal.nutrition?.carbs_g || selectedScheduledMeal.carbs_g || '0'}g</div><div className="text-gray-500">Carbs</div></div>
                <div><div className="font-bold text-gray-900">{selectedScheduledMeal.nutrition?.fat_g || selectedScheduledMeal.fat_g || '0'}g</div><div className="text-gray-500">Fat</div></div>
              </div>
            </div>
          )}

          {(selectedScheduledMeal.source_url || selectedScheduledMeal.spoonacular_url) && (
            <Button variant="outline" className="w-full mb-3 flex items-center justify-center gap-2" onClick={() => window.open(selectedScheduledMeal.source_url || selectedScheduledMeal.spoonacular_url, '_blank', 'noopener,noreferrer')}>
              <BookOpen className="h-4 w-4" />View Instructions<ExternalLink className="h-3 w-3" />
            </Button>
          )}

          <div className="space-y-2">
            <Button className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { closeScheduledMealModal(); openGroceryModal(selectedScheduledMeal); }} data-testid="scheduled-meal-grocery-btn">
              <ShoppingCart className="h-4 w-4" />Add to Grocery List
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 flex items-center justify-center gap-2 text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => setShowMoveModal(true)}><MoveRight className="h-4 w-4" />Move Date</Button>
              <Button variant="outline" className="flex-1 flex items-center justify-center gap-2 text-red-600 border-red-300 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)}><Trash2 className="h-4 w-4" />Delete</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────── Calendar Day View Modal ─────────── */
export const DayViewModal = ({ ctx }) => {
  const {
    showDayViewModal, selectedDayForView, closeDayViewModal,
    savedMeals, getMealImage, openScheduledMealModal
  } = ctx;

  if (!showDayViewModal || !selectedDayForView) return null;

  const dateKey = selectedDayForView.dateKey;
  const meals = [
    { type: 'breakfast', meal: savedMeals[`${dateKey}-breakfast`], color: 'orange', icon: '\u{1F305}', key: `${dateKey}-breakfast` },
    { type: 'lunch', meal: savedMeals[`${dateKey}-lunch`], color: 'green', icon: '\u{2600}\u{FE0F}', key: `${dateKey}-lunch` },
    { type: 'dinner', meal: savedMeals[`${dateKey}-dinner`], color: 'purple', icon: '\u{1F319}', key: `${dateKey}-dinner` }
  ];
  const scheduledMeals = meals.filter(m => m.meal);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className={`flex justify-between items-center p-4 border-b rounded-t-2xl ${
          selectedDayForView.isToday ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gradient-to-r from-gray-600 to-gray-700'
        }`}>
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><CalendarIcon className="h-5 w-5" />{selectedDayForView.dayName}</h2>
            <p className="text-sm text-white/80">
              {selectedDayForView.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              {selectedDayForView.isToday && <Badge className="ml-2 bg-white/20 text-white text-xs">Today</Badge>}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={closeDayViewModal} className="p-1 text-white hover:bg-white/20"><X className="h-5 w-5" /></Button>
        </div>

        <div className="p-4">
          {scheduledMeals.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><CalendarIcon className="h-8 w-8 text-gray-400" /></div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Meals Scheduled</h3>
              <p className="text-sm text-gray-500 mb-4">{selectedDayForView.isPast ? "This day has passed with no scheduled meals." : "You haven't scheduled any meals for this day yet."}</p>
              {!selectedDayForView.isPast && (
                <Button variant="outline" onClick={() => { closeDayViewModal(); window.scrollTo({ top: 500, behavior: 'smooth' }); }} className="text-blue-600 border-blue-300">
                  <Plus className="h-4 w-4 mr-2" />Browse Meals to Add
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><ChefHat className="h-4 w-4" />Scheduled Meals ({scheduledMeals.length})</h3>
              {meals.map((m, idx) => (
                <div key={idx}>
                  <div className={`flex items-center gap-2 mb-2 ${!m.meal ? 'opacity-50' : ''}`}>
                    <div className={`w-3 h-3 rounded-full ${m.color === 'orange' ? 'bg-orange-400' : m.color === 'green' ? 'bg-green-500' : 'bg-purple-500'}`}></div>
                    <span className="text-sm font-medium capitalize text-gray-700">{m.icon} {m.type}</span>
                  </div>
                  {m.meal ? (
                    <div onClick={() => { closeDayViewModal(); openScheduledMealModal(m.key, m.meal); }} className="bg-gray-50 rounded-xl p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200 hover:border-gray-300">
                      <SafeMealImage src={getMealImage(m.meal)} alt={m.meal.name} className="w-16 h-16 rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{m.meal.name}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-orange-500" />{m.meal.calories} cal</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-blue-500" />{m.meal.time || m.meal.readyInMinutes || '30'} min</span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-300 text-center"><p className="text-sm text-gray-400">Not scheduled</p></div>
                  )}
                </div>
              ))}

              {scheduledMeals.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Daily Summary</h4>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-orange-50 rounded-lg p-2 text-center"><div className="text-lg font-bold text-gray-900">{scheduledMeals.reduce((sum, m) => sum + (parseInt(m.meal.calories) || 0), 0)}</div><div className="text-xs text-gray-500">Calories</div></div>
                    <div className="bg-blue-50 rounded-lg p-2 text-center"><div className="text-lg font-bold text-gray-900">{scheduledMeals.reduce((sum, m) => sum + (parseFloat(m.meal.nutrition?.protein_g || m.meal.protein_g) || 0), 0).toFixed(0)}g</div><div className="text-xs text-gray-500">Protein</div></div>
                    <div className="bg-yellow-50 rounded-lg p-2 text-center"><div className="text-lg font-bold text-gray-900">{scheduledMeals.reduce((sum, m) => sum + (parseFloat(m.meal.nutrition?.carbs_g || m.meal.carbs_g) || 0), 0).toFixed(0)}g</div><div className="text-xs text-gray-500">Carbs</div></div>
                    <div className="bg-red-50 rounded-lg p-2 text-center"><div className="text-lg font-bold text-gray-900">{scheduledMeals.reduce((sum, m) => sum + (parseFloat(m.meal.nutrition?.fat_g || m.meal.fat_g) || 0), 0).toFixed(0)}g</div><div className="text-xs text-gray-500">Fat</div></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
          <Button variant="outline" className="w-full" onClick={closeDayViewModal}>Close</Button>
        </div>
      </div>
    </div>
  );
};

/* ─────────── Grocery List Integration Modal ─────────── */
export const GroceryModal = ({ ctx }) => {
  const {
    showGroceryModal, groceryMeal, closeGroceryModal, grocerySuccess,
    groceryMode, setGroceryMode, selectedIngredients, setSelectedIngredients,
    groceryIngredients, loadingIngredients, toggleIngredient, categorizeIngredient,
    selectedListId, setSelectedListId, lists, currentList,
    groceryScheduleMode, setGroceryScheduleMode,
    groceryScheduleDate, setGroceryScheduleDate, handleGroceryConfirm, getMealImage
  } = ctx;

  if (!showGroceryModal || !groceryMeal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><ShoppingCart className="h-5 w-5" />Add to Grocery List</h2>
          <Button variant="ghost" size="sm" onClick={closeGroceryModal} className="p-1 text-white hover:bg-white/20" data-testid="close-grocery-modal"><X className="h-5 w-5" /></Button>
        </div>

        {grocerySuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="h-8 w-8 text-green-600" /></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="grocery-success-title">{groceryMode === 'now' ? 'Added to Grocery List!' : 'Scheduled!'}</h3>
            <p className="text-gray-600">
              {groceryMode === 'now'
                ? `${selectedIngredients.length} ingredients added to "${(lists.find(l => l.id === selectedListId) || currentList)?.name || 'your grocery list'}".`
                : `Ingredients will be ${groceryScheduleMode === 'auto' ? 'automatically added' : 'reminded'} on ${groceryScheduleDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`
              }
            </p>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <SafeMealImage src={getMealImage(groceryMeal)} alt={groceryMeal.name} className="w-12 h-12 rounded-lg" />
              <div className="flex-1"><p className="font-semibold text-gray-900 text-sm line-clamp-1">{groceryMeal.name}</p><p className="text-xs text-gray-500">{groceryIngredients.length} ingredients</p></div>
            </div>

            {lists.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1.5">Add to list</p>
                <select value={selectedListId || ''} onChange={(e) => setSelectedListId(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500" data-testid="grocery-list-selector">
                  {lists.map((list) => (<option key={list.id} value={list.id}>{list.name} ({list.items.length} items)</option>))}
                </select>
              </div>
            )}

            <div className="flex gap-2 mb-4">
              <Button variant={groceryMode === 'now' ? 'default' : 'outline'} size="sm" onClick={() => setGroceryMode('now')} className={`flex-1 flex items-center justify-center gap-2 ${groceryMode === 'now' ? 'bg-amber-600 hover:bg-amber-700' : ''}`} data-testid="grocery-mode-now"><ListPlus className="h-4 w-4" />Add Now</Button>
              <Button variant={groceryMode === 'schedule' ? 'default' : 'outline'} size="sm" onClick={() => setGroceryMode('schedule')} className={`flex-1 flex items-center justify-center gap-2 ${groceryMode === 'schedule' ? 'bg-amber-600 hover:bg-amber-700' : ''}`} data-testid="grocery-mode-schedule"><Bell className="h-4 w-4" />Schedule</Button>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Select Ingredients
                  {loadingIngredients && <span className="ml-2 text-xs text-amber-500 animate-pulse">loading full list...</span>}
                </p>
                <button onClick={() => { selectedIngredients.length === groceryIngredients.length ? setSelectedIngredients([]) : setSelectedIngredients([...groceryIngredients]); }} className="text-xs text-amber-600 hover:text-amber-700 font-medium" data-testid="toggle-all-ingredients">
                  {selectedIngredients.length === groceryIngredients.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto border rounded-xl p-2 bg-gray-50">
                {groceryIngredients.map((ingredient, idx) => (
                  <label key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white cursor-pointer transition-colors" data-testid={`ingredient-item-${idx}`}>
                    <input type="checkbox" checked={selectedIngredients.includes(ingredient)} onChange={() => toggleIngredient(ingredient)} className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                    <span className="text-sm text-gray-800 capitalize">{ingredient}</span>
                    <Badge variant="outline" className="ml-auto text-xs">{categorizeIngredient(ingredient)}</Badge>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">{selectedIngredients.length} of {groceryIngredients.length} selected</p>
            </div>

            {groceryMode === 'schedule' && (
              <div className="mb-4 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">When the date arrives:</p>
                  <div className="flex gap-2">
                    <Button variant={groceryScheduleMode === 'alert' ? 'default' : 'outline'} size="sm" onClick={() => setGroceryScheduleMode('alert')} className={`flex-1 flex items-center justify-center gap-1 text-xs ${groceryScheduleMode === 'alert' ? 'bg-blue-600 hover:bg-blue-700' : ''}`} data-testid="schedule-mode-alert"><Bell className="h-3.5 w-3.5" />Alert Me</Button>
                    <Button variant={groceryScheduleMode === 'auto' ? 'default' : 'outline'} size="sm" onClick={() => setGroceryScheduleMode('auto')} className={`flex-1 flex items-center justify-center gap-1 text-xs ${groceryScheduleMode === 'auto' ? 'bg-blue-600 hover:bg-blue-700' : ''}`} data-testid="schedule-mode-auto"><ListPlus className="h-3.5 w-3.5" />Auto-Add</Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Select Date</p>
                  <div className="flex justify-center border rounded-xl p-2 bg-gray-50">
                    <Calendar mode="single" selected={groceryScheduleDate} onSelect={setGroceryScheduleDate} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-md" />
                  </div>
                </div>
                {groceryScheduleDate && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">{groceryScheduleMode === 'alert' ? 'Alert on: ' : 'Auto-add on: '}</span>
                      {groceryScheduleDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3" onClick={handleGroceryConfirm} disabled={selectedIngredients.length === 0 || (groceryMode === 'schedule' && !groceryScheduleDate)} data-testid="grocery-confirm-btn">
              {groceryMode === 'now' ? (<><ShoppingCart className="h-4 w-4 mr-2" />Add {selectedIngredients.length} Items to Grocery List</>) : (<><Bell className="h-4 w-4 mr-2" />Schedule {selectedIngredients.length} Items</>)}
            </Button>
            <button onClick={closeGroceryModal} className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-3 py-1" data-testid="skip-grocery-btn">Skip for now</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────── Due Schedule Alert Modal ─────────── */
export const DueAlertModal = ({ ctx }) => {
  const { showDueAlert, dueSchedules, currentDueIndex, handleDueAlertDismiss, handleDueAlertAdd } = ctx;

  if (!showDueAlert || !dueSchedules.length || !dueSchedules[currentDueIndex]) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden" data-testid="due-alert-modal">
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 animate-bounce"><Bell className="h-8 w-8 text-white" /></div>
          <h3 className="text-xl font-bold text-white">Grocery Reminder</h3>
          <p className="text-white/80 text-sm mt-1">{currentDueIndex + 1} of {dueSchedules.length} reminder{dueSchedules.length > 1 ? 's' : ''}</p>
        </div>
        <div className="p-5">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-1">Ingredients for:</p>
            <p className="font-bold text-gray-900 text-lg">{dueSchedules[currentDueIndex].meal_name}</p>
            <Badge className="mt-1 capitalize bg-amber-100 text-amber-700">{dueSchedules[currentDueIndex].meal_type}</Badge>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 mb-4 max-h-40 overflow-y-auto">
            <p className="text-xs font-medium text-gray-500 mb-2">{dueSchedules[currentDueIndex].ingredients.length} ingredients:</p>
            <div className="flex flex-wrap gap-1.5">
              {dueSchedules[currentDueIndex].ingredients.map((ing, idx) => (<Badge key={idx} variant="outline" className="capitalize text-xs">{ing}</Badge>))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 text-gray-600" onClick={handleDueAlertDismiss} data-testid="due-alert-dismiss">Dismiss</Button>
            <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleDueAlertAdd} data-testid="due-alert-add"><ShoppingCart className="h-4 w-4 mr-2" />Add to List</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
